/* eslint-disable object-curly-newline */
import { Modal, ModalContent, ModalOverlay, useToast } from '@chakra-ui/react';
import { useState } from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import { FormProvider, useForm } from 'react-hook-form';
import { getUnixTime } from 'date-fns';
import { useHoldingRequestMutation } from './use-holding-request-mutation';
import {
    RequestHoldingServiceFormFormValues,
    requestHoldingServiceFormSchema,
} from './form-schema';
import { RequestServiceOnHoldScreen } from './request-screen';
import { ConfirmServiceOnHoldScreen } from './confirmation-screen';

export function RequestServiceOnHoldModal({
    propertyId,
    defaultServices = [],
    isOpen,
    onClose,
}: {
    propertyId: number;
    defaultServices?: number[];
    isOpen: boolean;
    onClose: () => void;
}) {
    const [screen, setScreen] = useState<'REQUEST' | 'CONFIRM'>('REQUEST');

    const toast = useToast();
    const form = useForm<RequestHoldingServiceFormFormValues>({
        resolver: zodResolver(requestHoldingServiceFormSchema),
        defaultValues: {
            services: defaultServices,
        },
    });

    const handleClose = () => {
        form.reset();
        onClose();
        setScreen('REQUEST');
    };

    const holdingServiceMutation = useHoldingRequestMutation(propertyId, {
        onSuccess: () => {
            toast({
                status: 'success',
                title: 'Holding request submitted',
                description:
                    'Your request has been submitted successfully. You will receive a confirmation email when the property has been put on hold',
            });
            handleClose();
        },
    });

    const handleSubmit = () => {
        setScreen('CONFIRM');
    };

    const handleBack = () => {
        holdingServiceMutation.reset();
        setScreen('REQUEST');
    };

    const handleConfirm = (vals: RequestHoldingServiceFormFormValues) => {
        holdingServiceMutation.mutate({
            services: vals.services,
            holdDate: getUnixTime(vals.holdDate),
            reasonNote: vals.reasonNote,
        });
    };

    return (
        <Modal
            isOpen={isOpen}
            autoFocus={false}
            closeOnEsc={!holdingServiceMutation.isPending}
            closeOnOverlayClick={!holdingServiceMutation.isPending}
            onClose={handleClose}
            size="2xl"
        >
            <ModalOverlay />
            <ModalContent>
                <FormProvider {...form}>
                    {screen === 'REQUEST' ? (
                        <RequestServiceOnHoldScreen
                            propertyId={propertyId}
                            onClose={handleClose}
                            onSubmit={handleSubmit}
                        />
                    ) : null}
                    {screen === 'CONFIRM' ? (
                        <ConfirmServiceOnHoldScreen
                            propertyId={propertyId}
                            onSubmit={handleConfirm}
                            onBack={handleBack}
                            isLoading={holdingServiceMutation.isPending}
                            error={holdingServiceMutation.error}
                        />
                    ) : null}
                </FormProvider>
            </ModalContent>
        </Modal>
    );
}
